import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { TestDetailShimmer } from '../components/shimmer';
import { useGetItemByIdQuery, useGetTestsListQuery } from '../store/edosApi';
import { useListOffersQuery, useListFaqsQuery, useGetContactInfoQuery } from '../store/siteContentApi';
import { mapApiToTestDetail } from '../utils/mapApiDetail';
import { resolvePricing } from '../utils/offerLookup';
import { useCart } from '../context/CartContext';
import { useUI } from '../context/UIContext';
import { buildRoute, ROUTES } from '../constants/routes';
import { UI, DISPLAY, EASE } from '../styles/tl5';

// Test detail — built to the v5 reference.
//
// Two columns: the long read on the left, a sticky booking card on the
// right, and the same fixed "N tests in your visit" bar the Tests page
// uses once something is added.
//
// Three sections the reference fills from curated copy that the eDOS row
// has no field for, and what happens to them here:
//   · "What this test checks" — the reference prints a code, a name and a
//     plain-English line per parameter. We have only the names, so the
//     code column is dropped rather than invented.
//   · "Understanding your result" — the reference lists a typical adult
//     range per parameter. The catalogue carries no reference ranges, so
//     the rows are left out and the section keeps its explanation, the
//     flag legend and the free doctor-call band, all of which are true.
//   · "Questions about this test" — there are no per-test FAQs on the
//     row, so this shows the admin's site-wide FAQ list. Those are the
//     booking and reporting questions people actually ask here; when a
//     per-test field exists, read it instead.

const FALLBACK_CONTACT = { phone: '1800 599 5656', whatsapp: '+919014638633' };

const RANGE_LEGEND = [
  { c: 'var(--tl-teal)', t: 'Within range' },
  { c: '#F2B400', t: 'Borderline' },
  { c: '#D9534F', t: 'Needs attention' },
];

const sectionCard = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1.75,
  p: 'clamp(22px, 3.4vw, 36px)',
  borderRadius: '28px',
  bgcolor: '#FFFFFF',
};

const h2 = {
  m: 0,
  fontFamily: DISPLAY,
  fontWeight: 700,
  fontSize: 28,
  letterSpacing: '-.02em',
  color: 'var(--tl-g9)',
};

const TestDetail = () => {
  const { id } = useParams();
  const { toggleItem, inCart, totals } = useCart();
  const { showToast } = useUI();
  const [openFaq, setOpenFaq] = useState(0);

  const { data, isLoading, isError, error } = useGetItemByIdQuery(id, { skip: !id });
  const rawTest = mapApiToTestDetail(data);

  // Overlay any admin-curated offer for this test. If one is live we swap
  // `price` for the discounted value and keep `original` as the EDOS mrp.
  const { data: offers = [] } = useListOffersQuery();
  const pricing = rawTest ? resolvePricing(offers, rawTest.id, rawTest.price) : null;
  const test = rawTest && pricing
    ? { ...rawTest, price: pricing.finalPrice, original: pricing.originalPrice }
    : rawTest;

  const { data: siteFaqs = [] } = useListFaqsQuery();
  const { data: contact } = useGetContactInfoQuery();

  // "Often booked together" has no endpoint. The nearest real signal the
  // catalogue has is the department, so this is the rest of that bench.
  const { data: siblings } = useGetTestsListQuery(
    { page: 1, perPage: 5, department: test?.department },
    { skip: !test?.department || test.department === '—' },
  );

  if (isLoading) return <TestDetailShimmer />;

  if (isError || !test || data?.notFound) {
    return (
      <Box sx={{ maxWidth: 1320, mx: 'auto', px: 3, py: 10, textAlign: 'center' }}>
        <Typography component="h1" sx={{ ...h2, fontSize: 32, mb: 1.5 }}>Test not found</Typography>
        <Typography sx={{ fontFamily: UI, color: 'var(--tl-muted)', mb: 3 }}>
          {error?.data?.message || 'The link may be out of date, or the test was renamed.'}
        </Typography>
        <Box
          component={Link}
          to={ROUTES.TESTS}
          sx={{
            display: 'inline-block', p: '14px 22px', borderRadius: '999px',
            bgcolor: 'var(--tl-g9)', color: '#fff', fontFamily: UI, fontWeight: 800,
            textDecoration: 'none',
          }}
        >
          Browse all tests
        </Box>
      </Box>
    );
  }

  const isInCart = inCart(test.id, 'test');
  const hasPrice = Number(test.price) > 0;
  const hasDiscount = Number(test.original) > Number(test.price);

  const phone = contact?.phone || FALLBACK_CONTACT.phone;
  const whatsapp = String(contact?.whatsapp || FALLBACK_CONTACT.whatsapp).replace(/\D/g, '');

  const related = (siblings?.items || [])
    .filter((r) => String(r.id) !== String(test.id))
    .slice(0, 3)
    .map((r) => ({
      id: r.id,
      name: r.test_name,
      why: r.departments || r.department || 'Related test',
      price: Number(r.mrp) || 0,
      tat: r.report_tat || '',
    }));

  const facts = [
    { l: 'Sample', v: test.sampleType },
    { l: 'Fasting', v: test.fasting ? 'Required' : 'Not needed' },
    { l: 'Report', v: test.tat },
    { l: 'Parameters', v: String(test.paramCount) },
  ];

  // The catalogue's `diseases` column is the closest thing it has to
  // "who should take it" — the conditions this test is ordered for.
  const who = String(test.disease || '')
    .split(',').map((s) => s.trim()).filter((s) => s && s !== '—');

  const prep = [
    test.fasting
      ? { t: 'Fasting needed', d: `${test.fasting === true ? 'Follow the fasting window' : test.fasting}. Water is fine.` }
      : { t: 'No fasting needed', d: 'You can eat and drink normally before this test.' },
    { t: 'Sample', d: `${test.sampleType}${test.sampleVol && test.sampleVol !== '—' ? ` · ${test.sampleVol}` : ''}. Collected at home by a Trustian.` },
    test.instructions
      ? { t: 'Before your visit', d: test.instructions }
      : { t: 'Morning is best', d: 'Samples collected before noon are reported the same day.' },
  ];

  const handleCart = () => {
    toggleItem({
      id: test.id, type: 'test', name: test.name, price: test.price,
      original: test.original, icon: test.icon, tat: test.tat,
      paramCount: test.paramCount, fasting: !!test.fasting,
    });
    showToast(isInCart ? 'Removed from cart' : 'Added to cart');
  };

  const addRelated = (r) => {
    toggleItem({ id: r.id, type: 'test', name: r.name, price: r.price, tat: r.tat });
    showToast(inCart(r.id, 'test') ? 'Removed from cart' : 'Added to cart');
  };

  const crumb = { color: 'var(--tl-muted-2)', textDecoration: 'none', '&:hover': { color: 'var(--tl-g9)' } };

  return (
    <Box>
      {/* ── breadcrumb ───────────────────────────────────────────────── */}
      <Box
        sx={{
          width: '100%', maxWidth: 1320, mx: 'auto', pt: 2.5, px: 3,
          display: 'flex', flexWrap: 'wrap', gap: 1,
          fontFamily: UI, fontSize: 14, fontWeight: 600, color: 'var(--tl-muted-2)',
        }}
      >
        <Box component={Link} to={ROUTES.HOME} sx={crumb}>Home</Box>
        <span>/</span>
        <Box component={Link} to={ROUTES.TESTS} sx={crumb}>Tests</Box>
        <span>/</span>
        <Box component="span" sx={{ color: 'var(--tl-g9)' }}>{test.name}</Box>
      </Box>

      <Box
        component="main"
        sx={{
          width: '100%', maxWidth: 1320, mx: 'auto', px: 2,
          pt: 2, pb: totals.count > 0 ? '140px' : '72px',
          display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-start',
        }}
      >
        {/* ── left column ────────────────────────────────────────────── */}
        <Box sx={{ flex: '2 1 560px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {/* hero */}
          <Box
            component="section"
            sx={{
              display: 'flex', flexDirection: 'column', gap: 2.25,
              p: 'clamp(24px, 4vw, 44px)', borderRadius: '32px',
              bgcolor: 'var(--tl-teal)', color: '#FFFFFF',
            }}
          >
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Box
                component="span"
                sx={{
                  px: '10px', py: '6px', borderRadius: '999px', bgcolor: 'var(--tl-g9)',
                  fontFamily: UI, fontSize: 12, fontWeight: 800,
                }}
              >
                {test.category}
              </Box>
              {test.homeCol && (
                <Box
                  component="span"
                  sx={{
                    px: '10px', py: '6px', borderRadius: '999px',
                    bgcolor: 'var(--tl-yellow)', color: 'var(--tl-g9)',
                    fontFamily: UI, fontSize: 12, fontWeight: 800,
                  }}
                >
                  Home collection available
                </Box>
              )}
            </Box>

            <Typography
              component="h1"
              sx={{
                m: 0, fontFamily: DISPLAY, fontWeight: 700,
                fontSize: 'clamp(32px, 5.4vw, 60px)', lineHeight: 0.98,
                letterSpacing: '-.035em', color: '#FFFFFF', overflowWrap: 'anywhere',
              }}
            >
              {test.name}
            </Typography>

            {test.significance && (
              <Typography
                sx={{ m: 0, fontFamily: UI, fontSize: 17, lineHeight: 1.55, color: 'var(--tl-teal-50)', maxWidth: 560 }}
              >
                {test.significance}
              </Typography>
            )}

            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 130px), 1fr))', gap: 1 }}>
              {facts.map((f) => (
                <Box
                  key={f.l}
                  sx={{
                    display: 'flex', flexDirection: 'column', gap: 0.5,
                    p: '14px 16px', borderRadius: '18px', bgcolor: 'rgba(11,43,39,0.22)',
                  }}
                >
                  <Box component="span" sx={{ fontFamily: UI, fontSize: 12, fontWeight: 700, color: '#DFFBF4' }}>
                    {f.l}
                  </Box>
                  <Box component="span" sx={{ fontFamily: UI, fontSize: 16, fontWeight: 800 }}>
                    {f.v}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>

          {/* what this test checks */}
          {test.parameters.length > 0 && (
            <Box component="section" sx={sectionCard}>
              <Typography component="h2" sx={h2}>What this test checks</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {test.parameters.map((p) => (
                  <Box
                    key={p}
                    sx={{
                      display: 'flex', alignItems: 'baseline', gap: 1.75,
                      py: 1.75, borderTop: '1px solid #EEF3F1',
                    }}
                  >
                    <Box
                      component="span"
                      aria-hidden="true"
                      sx={{ flexShrink: 0, fontFamily: DISPLAY, fontWeight: 800, fontSize: 20, color: 'var(--tl-teal-d)' }}
                    >
                      ·
                    </Box>
                    <Box component="span" sx={{ fontFamily: UI, fontSize: 16, fontWeight: 800, color: 'var(--tl-g9)' }}>
                      {p}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* who should take it */}
          {who.length > 0 && (
            <Box component="section" sx={sectionCard}>
              <Typography component="h2" sx={h2}>Who should take it</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {who.map((w) => (
                  <Box
                    key={w}
                    component="span"
                    sx={{
                      p: '10px 14px', borderRadius: '999px', bgcolor: 'var(--tl-mint)',
                      fontFamily: UI, fontSize: 14, fontWeight: 700, color: 'var(--tl-g9)',
                    }}
                  >
                    {w}
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* how to prepare */}
          <Box component="section" sx={{ ...sectionCard, bgcolor: 'var(--tl-yellow)' }}>
            <Typography component="h2" sx={h2}>How to prepare</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap: 1 }}>
              {prep.map((p) => (
                <Box
                  key={p.t}
                  sx={{
                    display: 'flex', flexDirection: 'column', gap: 0.75,
                    p: 2, borderRadius: '18px', bgcolor: 'rgba(255,255,255,0.7)',
                  }}
                >
                  <Box component="span" sx={{ fontFamily: UI, fontSize: 16, fontWeight: 800, color: 'var(--tl-g9)' }}>
                    {p.t}
                  </Box>
                  <Box component="span" sx={{ fontFamily: UI, fontSize: 14, lineHeight: 1.5, color: '#3A4020' }}>
                    {p.d}
                  </Box>
                </Box>
              ))}
            </Box>
            <Box component="span" sx={{ fontFamily: UI, fontSize: 14, fontWeight: 700, color: '#3A4020' }}>
              We&rsquo;ll send these reminders on WhatsApp the evening before your visit.
            </Box>
          </Box>

          {/* understanding your result */}
          <Box component="section" sx={sectionCard}>
            <Typography component="h2" sx={h2}>Understanding your result</Typography>
            <Typography sx={{ m: 0, fontFamily: UI, fontSize: 15, lineHeight: 1.6, color: 'var(--tl-muted)' }}>
              Your report flags each value so you can see at a glance what needs attention.
              Reference ranges vary by age and lab; your doctor will interpret them for you.
            </Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, fontFamily: UI, fontSize: 13, fontWeight: 700, color: 'var(--tl-muted)' }}>
              {RANGE_LEGEND.map((l) => (
                <Box key={l.t} component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Box component="span" sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: l.c }} />
                  {l.t}
                </Box>
              ))}
            </Box>

            <Box
              sx={{
                display: 'flex', flexWrap: 'wrap', alignItems: 'center',
                justifyContent: 'space-between', gap: 2,
                p: '18px 20px', borderRadius: '20px',
                bgcolor: 'var(--tl-g9)', color: '#FFFFFF',
              }}
            >
              <Box component="span" sx={{ fontFamily: UI, fontSize: 15, lineHeight: 1.5, maxWidth: 460 }}>
                <strong>Result out of range?</strong> A TrustLab doctor will call you to
                explain it, free of charge.
              </Box>
              <Box
                component="span"
                sx={{
                  px: '12px', py: '8px', borderRadius: '999px',
                  bgcolor: 'var(--tl-teal-l)', color: 'var(--tl-g9)',
                  fontFamily: UI, fontSize: 13, fontWeight: 800, whiteSpace: 'nowrap',
                }}
              >
                Included
              </Box>
            </Box>
          </Box>

          {/* questions */}
          {siteFaqs.length > 0 && (
            <Box component="section" sx={{ ...sectionCard, gap: 0.75 }}>
              <Typography component="h2" sx={{ ...h2, mb: 1 }}>Questions about this test</Typography>
              {siteFaqs.slice(0, 5).map((f, i) => {
                const on = openFaq === i;
                return (
                  <Box key={f._id || f.question} sx={{ borderTop: '1px solid #EEF3F1' }}>
                    <Box
                      component="button"
                      type="button"
                      onClick={() => setOpenFaq(on ? -1 : i)}
                      aria-expanded={on}
                      sx={{
                        width: '100%', display: 'flex', justifyContent: 'space-between',
                        alignItems: 'center', gap: 2, py: 2.25,
                        background: 'none', border: 0, cursor: 'pointer', textAlign: 'left',
                        fontFamily: UI, fontSize: 16, fontWeight: 800, color: 'var(--tl-g9)',
                      }}
                    >
                      <Box component="span">{f.question}</Box>
                      <Box
                        component="span"
                        sx={{
                          display: 'grid', placeItems: 'center', flexShrink: 0,
                          width: 30, height: 30, borderRadius: '50%', bgcolor: 'var(--tl-mint)',
                        }}
                      >
                        {on ? '−' : '+'}
                      </Box>
                    </Box>
                    {on && (
                      <Typography sx={{ m: '0 0 18px', fontFamily: UI, fontSize: 15, lineHeight: 1.6, color: 'var(--tl-muted)' }}>
                        {f.answer}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>

        {/* ── right column ───────────────────────────────────────────── */}
        <Box
          component="aside"
          sx={{
            flex: '1 1 320px', minWidth: 0,
            display: 'flex', flexDirection: 'column', gap: 1.5,
            // Only sticks where there is a column beside it to scroll
            // against; stacked on a phone it would pin over the content.
            position: { md: 'sticky' },
            top: { md: 100 },
          }}
        >
          <Box
            sx={{
              display: 'flex', flexDirection: 'column', gap: 2,
              p: 3, borderRadius: '28px', bgcolor: '#FFFFFF',
              boxShadow: '0 20px 50px -30px rgba(11,43,39,0.4)',
            }}
          >
            {hasPrice ? (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1.5 }}>
                <Box component="span" sx={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 36, letterSpacing: '-.02em' }}>
                  ₹ {Number(test.price).toLocaleString('en-IN')}
                </Box>
                {hasDiscount && (
                  <Box
                    component="span"
                    sx={{ fontFamily: UI, fontSize: 14, fontWeight: 700, color: 'var(--tl-muted-2)', textDecoration: 'line-through' }}
                  >
                    ₹ {Number(test.original).toLocaleString('en-IN')}
                  </Box>
                )}
              </Box>
            ) : (
              <Box component="span" sx={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 26, color: 'var(--tl-muted-2)' }}>
                Price on request
              </Box>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, fontFamily: UI, fontSize: 15 }}>
              {[
                { l: 'Home collection', v: 'Free', teal: true },
                { l: 'Report', v: test.tat },
                { l: 'Doctor review', v: 'Included' },
              ].map((r) => (
                <Box key={r.l} component="span" sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5 }}>
                  <Box component="span" sx={{ color: 'var(--tl-muted-2)' }}>{r.l}</Box>
                  <Box
                    component="span"
                    sx={{ fontWeight: 800, textAlign: 'right', color: r.teal ? 'var(--tl-teal-d)' : 'inherit' }}
                  >
                    {r.v}
                  </Box>
                </Box>
              ))}
            </Box>

            <Box
              component="button"
              type="button"
              onClick={handleCart}
              sx={{
                border: 0, cursor: 'pointer', borderRadius: '16px', p: '17px',
                fontFamily: UI, fontSize: 17, fontWeight: 800,
                transition: `background-color .25s ${EASE}`,
                bgcolor: isInCart ? 'var(--tl-teal)' : 'var(--tl-mint)',
                color: isInCart ? '#FFFFFF' : 'var(--tl-g9)',
              }}
            >
              {isInCart ? 'Added to your visit ✓' : 'Add to visit'}
            </Box>

            <Box
              component={Link}
              to={ROUTES.BOOKING}
              onClick={() => { if (!isInCart) handleCart(); }}
              sx={{
                borderRadius: '16px', p: '17px', textAlign: 'center',
                bgcolor: 'var(--tl-g9)', color: '#FFFFFF',
                fontFamily: UI, fontSize: 17, fontWeight: 800, textDecoration: 'none',
                '&:hover': { bgcolor: 'var(--tl-g8)', color: '#FFFFFF' },
              }}
            >
              Book now
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, p: '14px 16px', borderRadius: '16px', bgcolor: 'var(--tl-mint)' }}>
              <Box component="span" sx={{ fontFamily: UI, fontSize: 14, fontWeight: 800, color: 'var(--tl-g9)' }}>
                Same-day report, guaranteed*
              </Box>
              <Box component="span" sx={{ fontFamily: UI, fontSize: 12, lineHeight: 1.5, color: 'var(--tl-muted-2)' }}>
                *Conditions apply. For most routine tests when your sample is collected before
                12 noon. Samples collected later are reported the next morning. Specialty tests
                take longer.
              </Box>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pt: 1.75, borderTop: '1px solid #EEF3F1' }}>
              <Box
                component="span"
                sx={{
                  fontFamily: UI, fontSize: 12, fontWeight: 800, letterSpacing: '.08em',
                  textTransform: 'uppercase', color: 'var(--tl-muted-2)',
                }}
              >
                More ways to book
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 0.75 }}>
                {[
                  { t: 'Call us', href: `tel:${String(phone).replace(/\D/g, '')}` },
                  { t: 'Get a callback', to: ROUTES.SUPPORT },
                  { t: 'WhatsApp', href: `https://wa.me/${whatsapp}`, ext: true },
                ].map((a) => (
                  <Box
                    key={a.t}
                    {...(a.to ? { component: Link, to: a.to } : { component: 'a', href: a.href })}
                    {...(a.ext ? { target: '_blank', rel: 'noopener noreferrer' } : null)}
                    sx={{
                      p: '12px 6px', borderRadius: '14px', textAlign: 'center',
                      bgcolor: 'var(--tl-mint)', color: 'var(--tl-g9)',
                      fontFamily: UI, fontSize: 13, fontWeight: 800, textDecoration: 'none',
                      '&:hover': { bgcolor: 'var(--tl-mint-2)', color: 'var(--tl-g9)' },
                    }}
                  >
                    {a.t}
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>

          {related.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 2.75, borderRadius: '28px', bgcolor: '#FFFFFF' }}>
              <Box component="span" sx={{ fontFamily: UI, fontSize: 16, fontWeight: 800, color: 'var(--tl-g9)' }}>
                Often booked together
              </Box>
              {related.map((r) => {
                const on = inCart(r.id, 'test');
                return (
                  <Box
                    key={r.id}
                    sx={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      gap: 1.5, py: 1.25, borderTop: '1px solid #EEF3F1',
                    }}
                  >
                    <Box
                      component={Link}
                      to={buildRoute(ROUTES.TEST_DETAIL, { id: r.id })}
                      sx={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0, textDecoration: 'none' }}
                    >
                      <Box component="span" sx={{ fontFamily: UI, fontSize: 15, fontWeight: 800, color: 'var(--tl-g9)' }}>
                        {r.name}
                      </Box>
                      <Box component="span" sx={{ fontFamily: UI, fontSize: 13, color: 'var(--tl-muted-2)' }}>
                        {r.why}
                      </Box>
                    </Box>
                    <Box
                      component="button"
                      type="button"
                      onClick={() => addRelated(r)}
                      sx={{
                        border: 0, cursor: 'pointer', borderRadius: '999px', p: '9px 14px',
                        whiteSpace: 'nowrap', fontFamily: UI, fontSize: 13, fontWeight: 800,
                        bgcolor: on ? 'var(--tl-teal)' : 'var(--tl-mint)',
                        color: on ? '#FFFFFF' : 'var(--tl-g9)',
                      }}
                    >
                      {on ? 'Added ✓' : '+ Add'}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </Box>

      {/* ── sticky visit bar ─────────────────────────────────────────── */}
      {totals.count > 0 && (
        <Box
          sx={{
            position: 'fixed', left: 12, right: { xs: 88, sm: 12 }, bottom: 12,
            zIndex: 500, display: 'flex', justifyContent: 'center', pointerEvents: 'none',
          }}
        >
          <Box
            sx={{
              pointerEvents: 'auto',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: 2, width: '100%', maxWidth: 560,
              p: '8px 8px 8px 22px', borderRadius: '999px',
              bgcolor: 'var(--tl-g9)', color: '#FFFFFF',
              boxShadow: '0 18px 40px -14px rgba(0,0,0,0.5)',
            }}
          >
            <Box component="span" sx={{ fontFamily: UI, fontSize: 15, fontWeight: 700, whiteSpace: 'nowrap' }}>
              {`${totals.count} test${totals.count === 1 ? '' : 's'}`}
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}> in your visit</Box>
            </Box>
            <Box
              component={Link}
              to={ROUTES.BOOKING}
              sx={{
                px: '20px', py: '12px', borderRadius: '999px',
                bgcolor: 'var(--tl-yellow)', color: 'var(--tl-g9)',
                fontFamily: UI, fontSize: 15, fontWeight: 800,
                textDecoration: 'none', whiteSpace: 'nowrap',
                '&:hover': { bgcolor: 'var(--tl-yellow-d)', color: 'var(--tl-g9)' },
              }}
            >
              Continue
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default TestDetail;
